import React, { useCallback, useEffect, useState } from "react";
import { api, apiError, BACKEND_URL } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Trash2,
  Rocket,
  Calendar,
  Search,
  Check,
  X,
  Send,
  AlertTriangle,
  Loader2,
  Pencil,
  Save,
  Tag,
  ChevronRight,
  ChevronDown,
  Clock,
  TrendingUp,
  ImageIcon,
} from "lucide-react";
import { EditedCoverButton } from "./production/EditedCoverButton";

const DAY_LABELS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

// Same palette used in ProductionDashboard.jsx for the per-staff color tag.
const COLOR_PALETTE = [
  { hex: "#ef4444", name: "Rosso" },
  { hex: "#f97316", name: "Arancio" },
  { hex: "#eab308", name: "Giallo" },
  { hex: "#22c55e", name: "Verde" },
  { hex: "#06b6d4", name: "Ciano" },
  { hex: "#3b82f6", name: "Blu" },
  { hex: "#a855f7", name: "Viola" },
  { hex: "#ec4899", name: "Rosa" },
];

function prettyDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function toLocalInput(date) {
  if (!date) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function defaultPublishWhen() {
  const d = new Date();
  d.setMinutes(d.getMinutes() + 10);
  return d;
}

export default function PostpickrQueue() {
  const [posts, setPosts] = useState([]);
  const [clientFilter, setClientFilter] = useState("");
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("queue"); // 'queue' = da pubblicare; 'published'
  const [loading, setLoading] = useState(false);
  const [openPost, setOpenPost] = useState(null);
  const [republishMode, setRepublishMode] = useState(false);
  // Per-session collapse map. ALL clients are collapsed by default.
  const [expandedClients, setExpandedClients] = useState(() => new Set());
  // Local cache so the chip flips immediately on pick.
  const [colorTags, setColorTags] = useState({}); // { clientId: "#hex" | null }
  // Copertura video nuovi (lato staff). Caricata in parallelo alla queue.
  const [coverage, setCoverage] = useState(null);
  const [coverageOpen, setCoverageOpen] = useState(true);
  // Paginazione server-side per il tab "Pubblicati" (grosso archivio).
  // La tab "queue" ritorna tutto in un colpo solo (lista corta per
  // definizione), "published" carica a chunk di 50.
  const PUBLISHED_PAGE_SIZE = 50;
  const [totalQueue, setTotalQueue] = useState(0);
  const [totalPublished, setTotalPublished] = useState(0);
  const [publishedHasMore, setPublishedHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const toggleClient = useCallback((clientId) => {
    if (!clientId) return;
    setExpandedClients((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  }, []);

  const setClientColor = useCallback(async (clientId, color) => {
    if (!clientId) return;
    setColorTags((prev) => ({ ...prev, [clientId]: color }));
    try {
      await api.put(`/staff/clients/${clientId}/color-tag`, { color });
    } catch (e) {
      toast.error(apiError(e));
      setColorTags((prev) => ({ ...prev, [clientId]: null }));
    }
  }, []);

  const load = useCallback(async (opts = {}) => {
    const { targetTab = tab, append = false } = opts;
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const params = { media_kind: "video", tab: targetTab };
      if (targetTab === "published") {
        params.limit = PUBLISHED_PAGE_SIZE;
        params.offset = append ? posts.length : 0;
      }
      const [postsRes, coverageRes] = await Promise.all([
        api.get("/staff/posts", { params }),
        // Coverage solo al primo load (non quando appendiamo pagine)
        append ? Promise.resolve({ data: null }) : api.get("/staff/rubrica/coverage").catch(() => ({ data: null })),
      ]);
      const items = postsRes.data.items || [];
      setPosts((prev) => (append ? [...prev, ...items] : items));
      setTotalQueue(postsRes.data.total_queue ?? 0);
      setTotalPublished(postsRes.data.total_published ?? 0);
      setPublishedHasMore(!!postsRes.data.has_more);
      if (!append && coverageRes) setCoverage(coverageRes?.data || null);
      // Hydrate colour tags from API (merge, non ricreare da zero).
      setColorTags((prev) => {
        const next = { ...prev };
        for (const it of items) {
          if (it.client_id && !(it.client_id in next)) {
            next[it.client_id] = it.client_color || null;
          }
        }
        return next;
      });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [tab, posts.length]);

  useEffect(() => {
    load();
  }, [load]);

  const clients = Array.from(
    new Map(
      posts.map((p) => [p.client_id, { id: p.client_id, name: p.client_name }])
    ).values()
  );

  const filtered = posts.filter((p) => {
    // Difesa client-side: dopo azioni locali (publish/schedule) lo status
    // nel ``posts`` locale può non coincidere con il tab (backend ha già
    // filtrato al load, ma qui filtriamo di nuovo per evitare drift finché
    // non ricarichiamo).
    if (p.status === "in_rubrica") return false;
    const isPublished = p.status === "published" || p.status === "scheduled";
    if (tab === "queue" && isPublished) return false;
    if (tab === "published" && !isPublished) return false;
    if (clientFilter && p.client_id !== clientFilter) return false;
    if (search) {
      const needle = search.toLowerCase();
      if (
        !(p.title || "").toLowerCase().includes(needle) &&
        !(p.caption || "").toLowerCase().includes(needle) &&
        !(p.client_name || "").toLowerCase().includes(needle)
      )
        return false;
    }
    return true;
  });

  // Conteggi dei tab: ora vengono dal backend (totali reali in DB), non più
  // da ``posts.filter`` (che vedeva solo la pagina corrente di published).
  const queueCount = totalQueue;
  const publishedCount = totalPublished;

  const groupedByClient = filtered.reduce((acc, p) => {
    const key = p.client_id || "unknown";
    if (!acc[key]) acc[key] = { name: p.client_name, items: [] };
    acc[key].items.push(p);
    return acc;
  }, {});
  // Pubblicati: ordina per scheduled_at DESC (più recente in alto, es. 08/05
  // sopra il 07/05). Queue mantiene l'ordine ASC originale dal backend.
  if (tab === "published") {
    Object.values(groupedByClient).forEach((g) => {
      g.items.sort((a, b) => {
        const da = a.scheduled_at ? new Date(a.scheduled_at).getTime() : 0;
        const db = b.scheduled_at ? new Date(b.scheduled_at).getTime() : 0;
        return db - da;
      });
    });
  }

  const removePost = async (id) => {
    if (!window.confirm("Eliminare questa bozza?")) return;
    try {
      await api.delete(`/staff/posts/${id}`);
      setPosts((prev) => prev.filter((p) => p.id !== id));
      toast.success("Bozza eliminata");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  // Dialog "Pulisci pubblicati vecchi" — admin only (il backend applica
  // il guard, lato UI mostriamo solo il bottone nel tab Pubblicati).
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleanupDays, setCleanupDays] = useState(14);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const openCleanup = async () => {
    setCleanupOpen(true);
    setCleanupPreview(null);
    try {
      setCleanupLoading(true);
      const { data } = await api.post("/admin/posts/cleanup-published", {
        older_than_days: cleanupDays,
        dry_run: true,
      });
      setCleanupPreview(data);
    } catch (e) {
      toast.error(apiError(e));
      setCleanupOpen(false);
    } finally {
      setCleanupLoading(false);
    }
  };
  const refreshPreview = async (days) => {
    try {
      setCleanupLoading(true);
      const { data } = await api.post("/admin/posts/cleanup-published", {
        older_than_days: days,
        dry_run: true,
      });
      setCleanupPreview(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCleanupLoading(false);
    }
  };
  const confirmCleanup = async () => {
    try {
      setCleanupLoading(true);
      const { data } = await api.post("/admin/posts/cleanup-published", {
        older_than_days: cleanupDays,
        dry_run: false,
      });
      toast.success(
        `Eliminati ${data.deleted} post pubblicati più vecchi di ${cleanupDays} giorni`
      );
      setCleanupOpen(false);
      // Ricarica la lista corrente.
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCleanupLoading(false);
    }
  };

  return (
    <div data-testid="metricool-queue-page" className="space-y-6 fade-up">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-[var(--mp-muted)]">
          Schedulazione
        </div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">
          Coda Video
        </h1>
        <p className="mt-3 text-[var(--mp-muted)] max-w-2xl">
          Bozze pronte per la pubblicazione e archivio dei post pubblicati. Clicca su una bozza per rivederla, modificare titolo/testo/hashtag e pubblicarla su Metricool.
        </p>
      </div>

      {/* Copertura video nuovi — vista staff per i clienti Pro con rubrica.
          Non modifica nulla della logica Coda Video; è solo un pannello
          informativo in testa alla pagina. */}
      {coverage && coverage.clients && coverage.clients.length > 0 && (
        <RubricaCoveragePanel
          data={coverage}
          open={coverageOpen}
          onToggle={() => setCoverageOpen((v) => !v)}
        />
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 rounded-full bg-white border border-[var(--mp-border)] p-1 w-fit">
        <button
          type="button"
          data-testid="queue-tab-queue"
          onClick={() => {
            if (tab !== "queue") {
              setTab("queue");
              load({ targetTab: "queue" });
            }
          }}
          className={`px-4 h-9 rounded-full text-xs font-medium transition-colors ${
            tab === "queue"
              ? "bg-[var(--mp-yellow)] text-[var(--mp-ink)]"
              : "text-[var(--mp-muted)] hover:text-[var(--mp-ink)]"
          }`}
        >
          Da pubblicare · {queueCount}
        </button>
        <button
          type="button"
          data-testid="queue-tab-published"
          onClick={() => {
            if (tab !== "published") {
              setTab("published");
              load({ targetTab: "published" });
            }
          }}
          className={`px-4 h-9 rounded-full text-xs font-medium transition-colors ${
            tab === "published"
              ? "bg-[var(--mp-ink)] text-white"
              : "text-[var(--mp-muted)] hover:text-[var(--mp-ink)]"
          }`}
        >
          Pubblicati · {publishedCount}
        </button>
        {tab === "published" && (
          <button
            type="button"
            data-testid="queue-cleanup-published-btn"
            onClick={openCleanup}
            title="Elimina post pubblicati più vecchi di N giorni (admin)"
            className="ml-2 px-3 h-9 rounded-full text-xs font-medium text-rose-700 border border-rose-200 hover:bg-rose-50 transition-colors inline-flex items-center gap-1.5"
          >
            <Trash2 className="h-3.5 w-3.5" /> Pulisci vecchi
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--mp-muted)]" />
          <Input
            data-testid="queue-search"
            placeholder="Cerca per titolo, caption o cliente…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 rounded-full border-[var(--mp-border)] h-10"
          />
        </div>
        <select
          data-testid="queue-client-filter"
          value={clientFilter}
          onChange={(e) => setClientFilter(e.target.value)}
          className="h-10 px-3 rounded-full border border-[var(--mp-border)] bg-white text-sm"
        >
          <option value="">Tutti i clienti</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name || "—"}
            </option>
          ))}
        </select>
      </div>

      {loading && posts.length === 0 ? (
        <div className="text-center py-20 text-[var(--mp-muted)]">Caricamento…</div>
      ) : Object.keys(groupedByClient).length === 0 ? (
        <div className="rounded-3xl border border-dashed border-[var(--mp-border)] bg-[var(--mp-dash-bg)] p-12 text-center">
          <Rocket className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <div className="font-display text-xl">
            {tab === "published"
              ? "Nessun post pubblicato ancora"
              : "Nessuna bozza in coda"}
          </div>
          <div className="text-sm text-[var(--mp-muted)] mt-1">
            {tab === "published"
              ? "I post inviati a Metricool appariranno qui."
              : 'Prepara un post dalla Produzione cliccando sul pulsante "Prepara".'}
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedByClient).map(([cid, group]) => {
            const isExpanded = expandedClients.has(cid);
            const tagColor = colorTags[cid] || null;
            return (
            <section
              key={cid}
              data-testid={`queue-group-${cid}`}
              className="rounded-3xl border border-[var(--mp-ink)] bg-white overflow-hidden"
            >
              <div className="px-5 py-3 flex items-center justify-between bg-[var(--mp-dash-bg)] border-b border-[var(--mp-border)]">
                <div className="flex items-center gap-2.5 min-w-0">
                  <button
                    type="button"
                    onClick={() => toggleClient(cid)}
                    data-testid={`queue-client-toggle-${cid}`}
                    title={isExpanded ? "Comprimi cliente" : "Espandi cliente"}
                    className="h-8 w-8 rounded-full grid place-items-center hover:bg-white/60 transition-colors shrink-0"
                  >
                    <ChevronRight
                      className={`h-4 w-4 transition-transform ${
                        isExpanded ? "rotate-90" : ""
                      }`}
                    />
                  </button>
                  <ClientColorChip
                    clientId={cid}
                    color={tagColor}
                    onChange={(c) => setClientColor(cid, c)}
                  />
                  <button
                    type="button"
                    onClick={() => toggleClient(cid)}
                    className="text-left min-w-0"
                  >
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
                      Cliente
                    </div>
                    <div className="font-display text-lg tracking-tight truncate">
                      {group.name || "—"}
                    </div>
                  </button>
                </div>
                <Badge className="rounded-full bg-[var(--mp-ink)] text-white text-[10px] uppercase tracking-[0.18em] shrink-0">
                  {group.items.length} {group.items.length === 1 ? "bozza" : "bozze"}
                </Badge>
              </div>
              {isExpanded && (
                <div className="divide-y divide-[var(--mp-border)]">
                  {group.items.map((p) => (
                    <PostRow
                      key={p.id}
                      post={p}
                      onOpen={() => {
                        setRepublishMode(false);
                        setOpenPost(p);
                      }}
                      onRepublish={() => {
                        setRepublishMode(true);
                        setOpenPost(p);
                      }}
                      onDelete={() => removePost(p.id)}
                    />
                  ))}
                </div>
              )}
            </section>
            );
          })}
          {tab === "published" && publishedHasMore && (
            <div className="flex justify-center pt-2">
              <Button
                type="button"
                variant="outline"
                data-testid="queue-load-more-published"
                onClick={() => load({ targetTab: "published", append: true })}
                disabled={loadingMore}
                className="rounded-full border-[var(--mp-ink)] text-[var(--mp-ink)]"
              >
                {loadingMore ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                Carica altri {PUBLISHED_PAGE_SIZE} · {posts.length} di {totalPublished}
              </Button>
            </div>
          )}
        </div>
      )}

      <MetricoolPublishDialog
        post={openPost}
        republishMode={republishMode}
        onClose={() => {
          setOpenPost(null);
          setRepublishMode(false);
        }}
        onUpdated={(updated) => {
          setPosts((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
          setOpenPost(updated);
        }}
        onPublished={(updated) => {
          setOpenPost(null);
          setRepublishMode(false);
          setPosts((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
          load();
        }}
      />

      {/* Dialog cleanup pubblicati — admin only, backend enforce */}
      {cleanupOpen && (
        <div
          data-testid="queue-cleanup-dialog"
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4"
          onClick={() => !cleanupLoading && setCleanupOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-3xl bg-white border-2 border-[var(--mp-ink)] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 bg-rose-50 border-b border-rose-200 flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-rose-600 grid place-items-center shrink-0">
                <AlertTriangle className="h-4 w-4 text-white" />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-rose-700">
                  Operazione distruttiva
                </div>
                <div className="font-display text-lg tracking-tight">
                  Pulisci pubblicati vecchi
                </div>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-[var(--mp-muted)]">
                Cancella dal portale i post già pubblicati su Metricool più vecchi del valore scelto. I file su Dropbox NON vengono toccati, solo il record nel portale. Operazione non annullabile.
              </p>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] mb-1.5 block">
                  Più vecchi di (giorni)
                </label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="1"
                    max="3650"
                    value={cleanupDays}
                    disabled={cleanupLoading}
                    data-testid="queue-cleanup-days"
                    onChange={(e) => {
                      const v = Math.max(1, Math.min(3650, parseInt(e.target.value) || 14));
                      setCleanupDays(v);
                    }}
                    onBlur={() => refreshPreview(cleanupDays)}
                    className="w-28 rounded-full border-[var(--mp-border)] h-10"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    disabled={cleanupLoading}
                    data-testid="queue-cleanup-refresh-preview"
                    onClick={() => refreshPreview(cleanupDays)}
                    className="rounded-full border-[var(--mp-border)] text-xs"
                  >
                    Ricalcola
                  </Button>
                </div>
              </div>
              {cleanupLoading ? (
                <div className="text-center py-3 text-sm text-[var(--mp-muted)]">
                  <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                  Conteggio in corso…
                </div>
              ) : cleanupPreview ? (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 space-y-1 text-sm">
                  <div>
                    Saranno cancellati:{" "}
                    <strong className="text-rose-700 text-base" data-testid="queue-cleanup-will-delete">
                      {cleanupPreview.will_delete}
                    </strong>{" "}
                    post
                  </div>
                  <div className="text-[var(--mp-muted)] text-xs">
                    Rimarranno: {cleanupPreview.will_remain} / Totale pubblicati:{" "}
                    {cleanupPreview.total_published}
                  </div>
                  <div className="text-[var(--mp-muted)] text-xs">
                    Taglio: posts con scheduled_at &lt;{" "}
                    {new Date(cleanupPreview.cutoff).toLocaleString("it-IT")}
                  </div>
                </div>
              ) : null}
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={cleanupLoading}
                  data-testid="queue-cleanup-cancel"
                  onClick={() => setCleanupOpen(false)}
                  className="rounded-full border-[var(--mp-border)]"
                >
                  Annulla
                </Button>
                <Button
                  type="button"
                  disabled={cleanupLoading || !cleanupPreview || cleanupPreview.will_delete === 0}
                  data-testid="queue-cleanup-confirm"
                  onClick={confirmCleanup}
                  className="rounded-full bg-rose-600 hover:bg-rose-700 text-white"
                >
                  {cleanupLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-2" />
                  )}
                  Cancella {cleanupPreview?.will_delete || 0} post
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PostRow({ post, onOpen, onRepublish, onDelete }) {
  const isPublished = post.status === "published" || post.status === "scheduled";
  return (
    <div
      data-testid={`queue-row-${post.id}`}
      className="p-4 sm:p-5 grid sm:grid-cols-[96px_1fr_180px_auto] gap-3 items-start hover:bg-[var(--mp-dash-bg)]/60 transition-colors cursor-pointer"
      onClick={onOpen}
      role="button"
    >
      <div className="h-20 w-20 rounded-xl bg-[var(--mp-dash-bg)] border border-[var(--mp-border)] grid place-items-center overflow-hidden">
        {post.media_url ? (
          <MediaPreview url={post.media_url} />
        ) : (
          <div className="text-[10px] text-[var(--mp-muted)] px-1 text-center">
            Nessun media
          </div>
        )}
      </div>
      <div className="min-w-0">
        {post.title && (
          <div className="font-display text-base tracking-tight truncate">
            {post.title}
          </div>
        )}
        {post.edited_cover && (
          <span className="inline-flex items-center gap-1 mt-1 text-[10px] uppercase tracking-[0.18em] text-emerald-700" data-testid={`queue-has-cover-${post.id}`}>
            <ImageIcon className="h-3 w-3" /> Copertina
          </span>
        )}
        <div className="text-sm whitespace-pre-wrap line-clamp-3 mt-0.5">
          {post.caption}
        </div>
        {post.hashtags?.length > 0 && (
          <div className="mt-2 text-[11px] text-[var(--mp-muted)] truncate">
            {post.hashtags.join(" ")}
          </div>
        )}
      </div>
      <div className="text-sm flex items-center gap-1.5 text-[var(--mp-muted)]">
        <Calendar className="h-3.5 w-3.5" />
        {prettyDate(post.scheduled_at)}
      </div>
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        {isPublished ? (
          <>
            <Badge className="rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 text-[10px] uppercase tracking-[0.18em]">
              <Check className="h-3 w-3 mr-1" />
              {post.status === "published" ? "Pubblicato" : "Programmato"}
            </Badge>
            <Button
              size="sm"
              onClick={onRepublish}
              data-testid={`queue-republish-${post.id}`}
              title="Ripubblica questo post"
              className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white h-8 text-xs"
            >
              <Send className="h-3.5 w-3.5 mr-1" />
              Ripubblica
            </Button>
          </>
        ) : (
          <Button
            size="sm"
            onClick={onOpen}
            data-testid={`queue-open-${post.id}`}
            title="Apri e pubblica"
            className="rounded-full bg-[var(--mp-yellow)] hover:bg-amber-300 text-[var(--mp-ink)] border border-[var(--mp-ink)] h-8 text-xs"
          >
            <Pencil className="h-3.5 w-3.5 mr-1" />
            Apri
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          onClick={onDelete}
          data-testid={`queue-delete-${post.id}`}
          className="h-8 w-8 p-0 text-[var(--mp-primary)]"
          title="Rimuovi"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function MediaPreview({ url }) {
  const full = url.startsWith("http") ? url : `${BACKEND_URL}${url}`;
  const isVideo = /\.(mp4|mov|webm|m4v)(\?|$)/i.test(url);
  // v1.58: ``onError`` ricarica l'elemento quando la temp URL Dropbox
  // scade (dopo ~4h di modale aperto). Il tag viene rimontato cambiando
  // la chiave, così il nuovo src riparte dalla redirect fresca.
  const [reloadNonce, setReloadNonce] = useState(0);
  if (isVideo) {
    return (
      <video
        key={reloadNonce}
        src={full}
        className="h-full w-full object-cover"
        controls
        preload="metadata"
        playsInline
        onError={() => setReloadNonce((n) => n + 1)}
      />
    );
  }
  return <img src={full} alt="media" className="h-full w-full object-cover" />;
}

function BestTimeHint({ clientId, providers, onPick }) {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);

  // Only query providers actually selected in the dialog (avoid useless calls)
  const activeProviders = Object.entries(providers || {})
    .filter(([k, v]) => v && ["instagram", "facebook", "tiktok", "youtube"].includes(k))
    .map(([k]) => k);
  const providersKey = activeProviders.join(",");

  useEffect(() => {
    if (!clientId || activeProviders.length === 0) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get(`/staff/clients/${clientId}/metricool/best-times`, {
        params: { providers: providersKey },
      })
      .then((r) => {
        if (cancelled) return;
        setResults(r.data.results || []);
      })
      .catch((e) => {
        if (cancelled) return;
        const msg = apiError(e);
        setError(msg);
        setResults([]);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, providersKey]);

  if (activeProviders.length === 0) return null;

  const PROVIDER_LABEL = {
    instagram: "Instagram",
    facebook: "Facebook",
    tiktok: "TikTok",
    youtube: "YouTube",
  };

  return (
    <div
      data-testid="best-time-hint"
      className="rounded-2xl border border-[var(--mp-border)] bg-[var(--mp-dash-bg)] p-3 space-y-2"
    >
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
        <Clock className="h-3 w-3" />
        Miglior orario · dai dati Metricool
      </div>

      {loading && (
        <div className="text-[12px] text-[var(--mp-muted)] flex items-center gap-1.5">
          <Loader2 className="h-3 w-3 animate-spin" /> Sto calcolando...
        </div>
      )}

      {error && !loading && (
        <div className="text-[12px] text-[var(--mp-muted)]">
          {error.includes("non collegato") || error.includes("400")
            ? "Cliente non ancora collegato a Metricool"
            : "Dati non disponibili al momento"}
        </div>
      )}

      {!loading && !error && results.length > 0 && (
        <div className="space-y-1.5">
          {results.map((r) => (
            <div key={r.provider} className="flex items-start gap-2 flex-wrap">
              <div className="min-w-[70px] text-[12px] font-semibold text-[var(--mp-ink)]">
                {PROVIDER_LABEL[r.provider] || r.provider}
              </div>
              {r.has_data && r.top_slots.length > 0 ? (
                <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
                  {r.top_slots.map((s, i) => (
                    <button
                      key={`${r.provider}-${s.day_of_week}-${s.hour}`}
                      type="button"
                      data-testid={`best-time-${r.provider}-${i}`}
                      onClick={() => onPick(s)}
                      className="text-[11px] px-2 py-1 rounded-full border border-[var(--mp-ink)] bg-white hover:bg-[var(--mp-yellow)] transition-colors font-medium tracking-tight"
                      title={`Punteggio: ${s.value}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-[12px] text-[var(--mp-muted)] italic flex-1">
                  {r.reason || "Nessun dato disponibile"}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!loading && !error && results.length === 0 && (
        <div className="text-[12px] text-[var(--mp-muted)]">
          Nessun social selezionato — i suggerimenti compaiono attivando almeno
          una piattaforma tra Instagram, Facebook, TikTok, YouTube.
        </div>
      )}
    </div>
  );
}


function MetricoolPublishDialog({ post, republishMode = false, onClose, onUpdated, onPublished }) {
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [hashtags, setHashtags] = useState("");
  const [scheduleAt, setScheduleAt] = useState("");
  const [providers, setProviders] = useState({
    instagram: true,
    facebook: true,
    tiktok: true,
    youtube: true,
    gmb: false,
  });
  // 4-way mode: "publish_and_recycle" | "rubrica_one_shot" | "schedule_via_rubrica" (default) | "schedule_via_rubrica_bottom"
  // Default lands on "schedule_via_rubrica" because the agency's main
  // workflow is now: feed the rubrica queue first, let the cron publish
  // on schedule. Hitting "publish now" by accident on every post would
  // bypass the rubrica completely.
  const [rubricaMode, setRubricaMode] = useState("schedule_via_rubrica");
  const [savingDraft, setSavingDraft] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [cover, setCover] = useState(null);

  useEffect(() => {
    if (!post) return;
    setCover(post.edited_cover || null);
    setTitle(post.title || "");
    setCaption(post.caption || "");
    setHashtags((post.hashtags || []).join(" "));
    // Republish: always reset to default (now+10min). View existing post: keep its date.
    if (republishMode) {
      setScheduleAt(toLocalInput(defaultPublishWhen()));
    } else if (post.scheduled_at) {
      try {
        setScheduleAt(toLocalInput(new Date(post.scheduled_at)));
      } catch {
        setScheduleAt(toLocalInput(defaultPublishWhen()));
      }
    } else {
      setScheduleAt(toLocalInput(defaultPublishWhen()));
    }
    setProviders({ instagram: true, facebook: true, tiktok: true, youtube: true, gmb: false });
    setRubricaMode("schedule_via_rubrica");
    // We intentionally only re-run this effect when the post identity or
    // republish toggle change — re-running on every prop ref would clobber
    // edits while the dialog is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post?.id, republishMode]);

  if (!post) return null;

  // Republish mode unlocks editing/publishing even if status is published/scheduled.
  const isLocked = !republishMode && (post.status === "published" || post.status === "scheduled");

  const togglerProvider = (key) => () =>
    setProviders((prev) => ({ ...prev, [key]: !prev[key] }));

  const buildTagArr = () =>
    hashtags
      .split(/\s+/)
      .map((t) => t.trim())
      .filter(Boolean)
      .map((t) => (t.startsWith("#") ? t : "#" + t))
      .slice(0, 5);

  // Applies a "best time" slot from Metricool: finds the next occurrence
  // of that (dayOfWeek + hour) starting from tomorrow, keeps minutes at :00.
  // Metricool dayOfWeek: 1=Sunday, 2=Monday, ..., 7=Saturday.
  const applyBestSlot = (slot) => {
    const target = ((slot.day_of_week - 1) + 7) % 7; // 0=Sun..6=Sat
    const now = new Date();
    // Start from tomorrow to avoid picking "today past-hour" surprises.
    const candidate = new Date(now);
    candidate.setDate(candidate.getDate() + 1);
    candidate.setHours(slot.hour, 0, 0, 0);
    // Walk forward until we hit the desired weekday
    while (candidate.getDay() !== target) {
      candidate.setDate(candidate.getDate() + 1);
    }
    setScheduleAt(toLocalInput(candidate));
    toast.success(`Orario impostato: ${slot.label}`);
  };

  const saveDraft = async () => {
    setSavingDraft(true);
    try {
      const body = {
        title: title.trim(),
        caption,
        hashtags: buildTagArr(),
        scheduled_at: scheduleAt
          ? new Date(scheduleAt).toISOString()
          : null,
      };
      const { data } = await api.patch(`/staff/posts/${post.id}`, body);
      toast.success("Bozza aggiornata");
      onUpdated && onUpdated({ ...data.post, edited_cover: cover });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSavingDraft(false);
    }
  };

  const publish = async () => {
    const list = Object.entries(providers)
      .filter(([, v]) => v)
      .map(([k]) => k);
    if (list.length === 0) {
      toast.error("Seleziona almeno una piattaforma");
      return;
    }
    setPublishing(true);
    try {
      // Save current edits first
      await api.patch(`/staff/posts/${post.id}`, {
        title: title.trim(),
        caption,
        hashtags: buildTagArr(),
        scheduled_at: scheduleAt
          ? new Date(scheduleAt).toISOString()
          : null,
      });
      // Then publish — auto_publish is now ALWAYS true (UI checkbox removed
      // because staff kept forgetting to tick it; the only mode the agency
      // ever uses is "submit & let Metricool fan out").
      const body = {
        providers: list,
        auto_publish: true,
        rubrica_mode: rubricaMode,
        schedule_date: scheduleAt
          ? new Date(scheduleAt).toISOString()
          : null,
      };
      const { data } = await api.post(
        `/staff/posts/${post.id}/publish-metricool`,
        body
      );
      toast.success("Post inviato a Metricool — pubblicazione in corso");
      onPublished && onPublished(data.post);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Dialog open={!!post} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="metricool-publish-dialog"
        className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
            <Send className="h-5 w-5" /> {republishMode ? "Ripubblica su Metricool" : isLocked ? "Bozza pubblicata" : "Pubblica su Metricool"}
          </DialogTitle>
          <DialogDescription>
            Cliente: <strong>{post.client_name}</strong>
          </DialogDescription>
        </DialogHeader>

        {/* Video / media preview */}
        {post.media_url && (
          <div className="rounded-2xl border border-[var(--mp-border)] bg-[var(--mp-dash-bg)] p-2 flex items-center justify-center min-h-[180px]">
            <MediaLargePreview url={post.media_url} />
          </div>
        )}

        {/* Copertina → videoThumbnailUrl su Metricool (anche uscite Rubrica) */}
        {post.media_kind !== "foto" && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-[var(--mp-border)] px-3 py-2" data-testid="pub-cover-section">
            <div className="text-xs text-[var(--mp-muted)] max-w-[60%]">
              <strong className="text-[var(--mp-ink)]">Copertina</strong> · Reel Instagram/Facebook, TikTok, YouTube. Vale anche per le uscite future dalla Rubrica. Senza copertina il video esce come sempre.
            </div>
            {post.source_message_id ? (
              <EditedCoverButton
                messageId={post.source_message_id}
                cover={cover}
                testKey={post.id}
                onChanged={(c) => {
                  setCover(c);
                  onUpdated && onUpdated({ ...post, edited_cover: c });
                }}
              />
            ) : (
              <span className="text-xs text-[var(--mp-muted)]" data-testid="pub-cover-unavailable">Non disponibile: post senza video sorgente</span>
            )}
          </div>
        )}

        {/* Title */}
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] mb-1.5 block">
            Titolo
          </label>
          <Input
            data-testid="pub-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={isLocked}
            placeholder="Titolo del post"
            maxLength={60}
            className="rounded-full border-[var(--mp-border)] h-10"
          />
        </div>

        {/* Caption */}
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] mb-1.5 block">
            Caption
          </label>
          <Textarea
            data-testid="pub-caption"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            disabled={isLocked}
            className="min-h-[120px] rounded-2xl border-[var(--mp-border)]"
          />
        </div>

        {/* Hashtags */}
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] mb-1.5 block flex items-center gap-1">
            <Tag className="h-3 w-3" /> Hashtag (max 5, separati da spazio)
          </label>
          <Textarea
            data-testid="pub-hashtags"
            value={hashtags}
            onChange={(e) => setHashtags(e.target.value)}
            disabled={isLocked}
            className="min-h-[60px] rounded-2xl border-[var(--mp-border)] font-mono text-sm"
          />
        </div>

        {/* Schedule */}
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] mb-1.5 block flex items-center gap-1">
            <Calendar className="h-3 w-3" /> Data e ora pubblicazione
          </label>
          <Input
            type="datetime-local"
            data-testid="pub-when"
            value={scheduleAt}
            onChange={(e) => setScheduleAt(e.target.value)}
            disabled={isLocked}
            className="rounded-full border-[var(--mp-border)] h-10"
          />
          <div className="mt-1 text-[11px] text-[var(--mp-muted)]">
            Default: tra 10 minuti. Modificalo per programmare a una data futura.
          </div>
        </div>

        {!isLocked && (
          <BestTimeHint
            clientId={post.client_id}
            providers={providers}
            onPick={applyBestSlot}
          />
        )}

        {!isLocked && (
          <>
            {/* Providers */}
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] mb-2 block">
                Piattaforme
              </label>
              <div className="flex flex-wrap gap-2">
                {[
                  { k: "instagram", label: "Instagram" },
                  { k: "facebook", label: "Facebook" },
                  { k: "tiktok", label: "TikTok" },
                  { k: "youtube", label: "YouTube" },
                  { k: "gmb", label: "Google My Business" },
                ].map(({ k, label }) => (
                  <button
                    key={k}
                    type="button"
                    onClick={togglerProvider(k)}
                    data-testid={`pub-provider-${k}`}
                    className={`h-9 px-4 rounded-full border text-sm font-medium transition-colors ${
                      providers[k]
                        ? "bg-[var(--mp-ink)] border-[var(--mp-ink)] text-white"
                        : "bg-white border-[var(--mp-border)] text-[var(--mp-muted)] hover:border-[var(--mp-ink)]"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* 4-way mode selector */}
            <div className="rounded-2xl border-2 border-[var(--mp-ink)] bg-white p-3 space-y-2">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
                Modalità di uscita
              </div>
              <label
                className={`flex items-start gap-2 text-sm cursor-pointer select-none rounded-xl p-2 ${
                  rubricaMode === "publish_and_recycle"
                    ? "bg-[var(--mp-yellow)]/40 border border-[var(--mp-yellow)]"
                    : "border border-transparent"
                }`}
              >
                <input
                  type="radio"
                  name="rubrica-mode"
                  data-testid="pub-mode-recycle"
                  value="publish_and_recycle"
                  checked={rubricaMode === "publish_and_recycle"}
                  onChange={() => setRubricaMode("publish_and_recycle")}
                  className="mt-0.5 h-4 w-4 accent-[var(--mp-ink)]"
                />
                <span>
                  <strong>Pubblica adesso + metti in fondo alla rubrica</strong>{" "}
                  <span className="text-[var(--mp-muted)]">
                    (esce subito sui social, riciclato dopo gli altri)
                  </span>
                </span>
              </label>

              <label
                className={`flex items-start gap-2 text-sm cursor-pointer select-none rounded-xl p-2 ${
                  rubricaMode === "rubrica_one_shot"
                    ? "bg-[var(--mp-yellow)]/40 border border-[var(--mp-yellow)]"
                    : "border border-transparent"
                }`}
              >
                <input
                  type="radio"
                  name="rubrica-mode"
                  data-testid="pub-mode-oneshot"
                  value="rubrica_one_shot"
                  checked={rubricaMode === "rubrica_one_shot"}
                  onChange={() => setRubricaMode("rubrica_one_shot")}
                  className="mt-0.5 h-4 w-4 accent-[var(--mp-ink)]"
                />
                <span>
                  <strong>📚 Rubrica one-shot (testa, poi sparisce)</strong>{" "}
                  <span className="text-[var(--mp-muted)]">
                    esce al prossimo slot rubrica, nessun riciclo
                  </span>
                </span>
              </label>

              <label
                className={`flex items-start gap-2 text-sm cursor-pointer select-none rounded-xl p-2 ${
                  rubricaMode === "schedule_via_rubrica"
                    ? "bg-[var(--mp-yellow)]/40 border border-[var(--mp-yellow)]"
                    : "border border-transparent"
                }`}
              >
                <input
                  type="radio"
                  name="rubrica-mode"
                  data-testid="pub-mode-rubrica"
                  value="schedule_via_rubrica"
                  checked={rubricaMode === "schedule_via_rubrica"}
                  onChange={() => setRubricaMode("schedule_via_rubrica")}
                  className="mt-0.5 h-4 w-4 accent-[var(--mp-ink)]"
                />
                <span>
                  <strong>📚 Solo rubrica (testa)</strong>{" "}
                  <span className="text-[var(--mp-muted)]">
                    non pubblica subito; uscirà al prossimo slot del cron rubrica
                  </span>
                </span>
              </label>

              <label
                className={`flex items-start gap-2 text-sm cursor-pointer select-none rounded-xl p-2 ${
                  rubricaMode === "schedule_via_rubrica_bottom"
                    ? "bg-[var(--mp-yellow)]/40 border border-[var(--mp-yellow)]"
                    : "border border-transparent"
                }`}
              >
                <input
                  type="radio"
                  name="rubrica-mode"
                  data-testid="pub-mode-rubrica-bottom"
                  value="schedule_via_rubrica_bottom"
                  checked={rubricaMode === "schedule_via_rubrica_bottom"}
                  onChange={() => setRubricaMode("schedule_via_rubrica_bottom")}
                  className="mt-0.5 h-4 w-4 accent-[var(--mp-ink)]"
                />
                <span>
                  <strong>📚 Solo rubrica (coda)</strong>{" "}
                  <span className="text-[var(--mp-muted)]">
                    non pubblica subito; finisce in fondo alla rubrica, uscirà dopo tutti gli altri
                  </span>
                </span>
              </label>
            </div>

            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                Verifica che il salone sia collegato a un brand Metricool dalla Rubrica
                cliente.
              </span>
            </div>
          </>
        )}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-[var(--mp-border)] flex-wrap">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={savingDraft || publishing}
            className="rounded-full border-[var(--mp-ink)]"
            data-testid="pub-cancel"
          >
            <X className="h-4 w-4 mr-1" /> Chiudi
          </Button>
          {!isLocked && (
            <>
              <Button
                onClick={saveDraft}
                disabled={savingDraft || publishing}
                variant="outline"
                data-testid="pub-save-draft"
                className="rounded-full border-[var(--mp-ink)]"
              >
                {savingDraft ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-1" />
                )}
                {savingDraft ? "Salvo…" : "Salva bozza"}
              </Button>
              <Button
                onClick={publish}
                disabled={publishing || savingDraft}
                data-testid="pub-confirm"
                className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
              >
                {publishing ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-1" />
                )}
                {publishing
                  ? "Invio…"
                  : republishMode
                  ? "Ripubblica"
                  : (rubricaMode === "schedule_via_rubrica"
                      || rubricaMode === "schedule_via_rubrica_bottom"
                      || rubricaMode === "rubrica_one_shot")
                  ? "Metti in rubrica"
                  : "Pubblica"}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MediaLargePreview({ url }) {
  const full = url.startsWith("http") ? url : `${BACKEND_URL}${url}`;
  const isVideo = /\.(mp4|mov|webm|m4v)(\?|$)/i.test(url);
  // v1.58: ``onError`` ricarica l'elemento quando la temp URL Dropbox
  // scade (dopo ~4h di modale aperto). Il tag viene rimontato cambiando
  // la key.
  const [reloadNonce, setReloadNonce] = useState(0);
  if (isVideo) {
    return (
      <video
        key={reloadNonce}
        src={full}
        className="max-h-[280px] w-auto rounded-xl"
        controls
        preload="metadata"
        playsInline
        onError={() => setReloadNonce((n) => n + 1)}
      />
    );
  }
  return (
    <img
      src={full}
      alt="anteprima"
      className="max-h-[280px] w-auto rounded-xl"
    />
  );
}


function ClientColorChip({ clientId, color, onChange }) {
  const [open, setOpen] = useState(false);
  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!e.target.closest?.(`[data-color-chip="${clientId}"]`)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, clientId]);

  const dotStyle = color ? { background: color } : {};
  return (
    <div
      data-color-chip={clientId}
      className="relative shrink-0"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          setOpen((v) => !v);
        }}
        data-testid={`queue-client-color-${clientId}`}
        title="Cambia il colore di questo cliente"
        className={`h-7 w-7 rounded-full border-2 border-[var(--mp-ink)] transition-transform hover:scale-105 ${
          color ? "" : "bg-white"
        }`}
        style={dotStyle}
      />
      {open && (
        <div
          data-testid={`queue-client-color-popover-${clientId}`}
          className="absolute left-0 top-9 z-50 rounded-2xl border-2 border-[var(--mp-ink)] bg-white p-2 shadow-lg flex items-center gap-1.5"
        >
          {COLOR_PALETTE.map((c) => (
            <button
              key={c.hex}
              type="button"
              onClick={() => {
                onChange(c.hex);
                setOpen(false);
              }}
              title={c.name}
              data-testid={`queue-client-color-pick-${clientId}-${c.hex.replace("#", "")}`}
              className={`h-6 w-6 rounded-full border-2 hover:scale-110 transition-transform ${
                color === c.hex ? "border-[var(--mp-ink)]" : "border-transparent"
              }`}
              style={{ background: c.hex }}
            />
          ))}
          {color && (
            <button
              type="button"
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
              title="Rimuovi colore"
              data-testid={`queue-client-color-clear-${clientId}`}
              className="h-6 w-6 rounded-full border-2 border-[var(--mp-border)] bg-white grid place-items-center hover:bg-stone-50"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// RubricaCoveragePanel — vista sintetica staff dei "giorni video nuovi".
// Mostra per ogni cliente Pro, in ordine alfabetico, due righe (settimana
// corrente / successiva) di 7 pallini con lo stato di copertura.
// Non ha logica di modifica: configurazione in /dashboard/rubrica.
// ---------------------------------------------------------------------------
function CoverageDot({ slot, label }) {
  // slot = { date, weekday, kind: null|"replica"|"new", covered: null|true|false, past, will_publish }
  const base = "h-5 w-5 rounded-full grid place-items-center text-[9px] font-bold select-none";
  let style = {};
  let title = `${label} ${new Date(slot.date).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })}`;
  let marker = label.charAt(0);
  if (slot.kind === null) {
    style = { background: "#f1efe9", color: "#b0ac9f" };
    title += " — giorno non in rubrica";
  } else if (slot.past) {
    style = { background: "#e5e7eb", color: "#6b7280" };
    title += slot.kind === "new" ? " — giorno nuovo passato" : " — giorno rubrica passato";
  } else if (slot.kind === "replica") {
    if (slot.will_publish === false) {
      style = { background: "#fbd5d5", color: "#9b1c1c", border: "1px dashed #dc2626" };
      title += " — giorno replica SALTATO (repliche spente)";
      marker = "✕";
    } else {
      style = { background: "#c7d2fe", color: "#1e3a8a" };
      title += " — giorno replica";
    }
  } else if (slot.kind === "new") {
    if (slot.covered === true) {
      style = { background: "#16a34a", color: "#ffffff" };
      title += " — nuovo coperto";
    } else if (slot.will_publish === false) {
      // nuovo scoperto + repliche spente → saltato
      style = { background: "#fbd5d5", color: "#9b1c1c", border: "1px dashed #dc2626" };
      title += " — nuovo scoperto, SALTATO (repliche spente)";
      marker = "✕";
    } else {
      // nuovo scoperto + repliche attive → esce replica di fallback
      style = { background: "#dc2626", color: "#ffffff" };
      title += " — nuovo scoperto (uscirà replica)";
    }
  }
  return (
    <div
      className={base}
      style={style}
      title={title}
      data-testid={`coverage-dot-${slot.date}`}
    >
      {marker}
    </div>
  );
}

function CoverageRow({ slots, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-[var(--mp-muted)] w-14">
        {label}
      </div>
      <div className="flex gap-1">
        {slots.map((s, i) => (
          <CoverageDot key={s.date} slot={s} label={DAY_LABELS[i]} />
        ))}
      </div>
    </div>
  );
}

function RubricaCoveragePanel({ data, open, onToggle }) {
  const clients = data.clients || [];
  const [coverageSearch, setCoverageSearch] = useState("");
  const needle = coverageSearch.trim().toLowerCase();
  const filteredClients = needle
    ? clients.filter((c) => (c.client_name || "").toLowerCase().includes(needle))
    : clients;
  const totalCoveredThis = clients.reduce(
    (acc, c) => acc + (c.metrics_this_week?.covered || 0),
    0
  );
  const totalSlotsThis = clients.reduce(
    (acc, c) => acc + (c.metrics_this_week?.new_slots || 0),
    0
  );
  const uncoveredThis = clients.filter(
    (c) =>
      (c.metrics_this_week?.new_slots || 0) >
      (c.metrics_this_week?.covered || 0)
  ).length;

  return (
    <section
      data-testid="rubrica-coverage-panel"
      className="rounded-3xl border-2 border-[var(--mp-ink)] bg-white overflow-hidden"
    >
      <button
        type="button"
        onClick={onToggle}
        data-testid="rubrica-coverage-toggle"
        className="w-full px-5 py-3 flex items-center justify-between bg-[var(--mp-dash-bg)] border-b border-[var(--mp-border)] hover:bg-white/60 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-8 w-8 rounded-full bg-[var(--mp-yellow)] grid place-items-center border border-[var(--mp-ink)]">
            <TrendingUp className="h-4 w-4" />
          </div>
          <div className="text-left">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
              Copertura video nuovi · {clients.length} client{clients.length === 1 ? "e" : "i"} Pro
            </div>
            <div className="font-display text-base tracking-tight">
              Questa settimana: {totalCoveredThis}/{totalSlotsThis} slot nuovi coperti
              {uncoveredThis > 0 && (
                <span className="ml-2 text-rose-600 text-sm">
                  · {uncoveredThis} client{uncoveredThis === 1 ? "e" : "i"} scoperti
                </span>
              )}
            </div>
          </div>
        </div>
        <ChevronDown
          className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="p-4 space-y-3">
          {/* Barra ricerca cliente — client-side su lista già alfabetica */}
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--mp-muted)]" />
            <Input
              data-testid="coverage-search"
              placeholder="Cerca cliente nella copertura…"
              value={coverageSearch}
              onChange={(e) => setCoverageSearch(e.target.value)}
              className="pl-9 rounded-full border-[var(--mp-border)] h-9"
            />
          </div>
          {/* Legenda */}
          <div className="flex items-center gap-3 flex-wrap text-[11px] text-[var(--mp-muted)]">
            <div className="flex items-center gap-1.5">
              <div className="h-3 w-3 rounded-full" style={{ background: "#16a34a" }} />
              Nuovo coperto
            </div>
            <div className="flex items-center gap-1.5">
              <div className="h-3 w-3 rounded-full" style={{ background: "#dc2626" }} />
              Nuovo → replica
            </div>
            <div className="flex items-center gap-1.5">
              <div className="h-3 w-3 rounded-full" style={{ background: "#c7d2fe" }} />
              Giorno replica
            </div>
            <div className="flex items-center gap-1.5">
              <div className="h-3 w-3 rounded-full" style={{ background: "#fbd5d5", border: "1px dashed #dc2626" }} />
              Saltato (repliche spente)
            </div>
            <div className="flex items-center gap-1.5">
              <div className="h-3 w-3 rounded-full" style={{ background: "#e5e7eb" }} />
              Passato
            </div>
            <div className="flex items-center gap-1.5">
              <div className="h-3 w-3 rounded-full" style={{ background: "#f1efe9" }} />
              Non in rubrica
            </div>
          </div>
          {/* Lista clienti */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {filteredClients.length === 0 ? (
              <div className="col-span-full text-center text-sm text-[var(--mp-muted)] py-6">
                Nessun cliente corrisponde a "{coverageSearch}".
              </div>
            ) : (
              filteredClients.map((c) => {
              const en = c.settings?.enabled;
              const hhmm = `${String(c.settings?.hour ?? 11).padStart(2, "0")}:${String(c.settings?.minute ?? 0).padStart(2, "0")}`;
              return (
                <div
                  key={c.client_id}
                  data-testid={`coverage-client-${c.client_id}`}
                  className="rounded-2xl border border-[var(--mp-border)] bg-white p-3"
                >
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="font-display text-sm tracking-tight truncate">
                      {c.client_name}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {c.to_check_count > 0 && (
                        <span
                          data-testid={`coverage-to-check-${c.client_id}`}
                          title="Video del cliente ancora da controllare (status: todo)"
                          className="inline-flex items-center h-5 px-2 rounded-full text-[10px] font-semibold tracking-wider uppercase bg-sky-100 text-sky-800 border border-sky-300"
                        >
                          🔍 Da controllare: {c.to_check_count}
                        </span>
                      )}
                      {c.to_edit_count > 0 && (
                        <span
                          data-testid={`coverage-to-edit-${c.client_id}`}
                          title="Video controllati che vanno passati al montaggio manuale"
                          className="inline-flex items-center h-5 px-2 rounded-full text-[10px] font-semibold tracking-wider uppercase bg-amber-100 text-amber-800 border border-amber-300"
                        >
                          🎬 Da montare: {c.to_edit_count}
                        </span>
                      )}
                      <span
                        className={`inline-flex items-center h-5 px-2 rounded-full text-[10px] font-semibold tracking-wider uppercase ${
                          en
                            ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                            : "bg-stone-100 text-stone-600 border border-stone-300"
                        }`}
                      >
                        Rubrica {en ? "ON" : "OFF"}
                      </span>
                      <span className="text-[10px] text-[var(--mp-muted)]">
                        {hhmm}
                      </span>
                    </div>
                  </div>
                  <div className="text-[11px] text-[var(--mp-muted)] mb-2">
                    Nuovi: <strong>{c.settings?.new_days?.length || 0}</strong>/
                    {c.settings?.days?.length || 0} giorni ·{" "}
                    Disponibili: <strong>{c.new_available}</strong> item nuovi
                    {" · "}
                    Settimana: {c.metrics_this_week?.covered || 0}/
                    {c.metrics_this_week?.new_slots || 0} ·{" "}
                    Prossima: {c.metrics_next_week?.covered || 0}/
                    {c.metrics_next_week?.new_slots || 0}
                  </div>
                  <div className="space-y-1">
                    <CoverageRow slots={c.coverage_this_week || []} label="Questa" />
                    <CoverageRow slots={c.coverage_next_week || []} label="Prossima" />
                  </div>
                </div>
              );
            })
            )}
          </div>
        </div>
      )}
    </section>
  );
}

